import {
  Badge,
  Button,
  Dot,
  MultiSelect,
  SlideoutMenu,
  Toggle,
  Tooltip,
  TooltipTrigger,
} from "@light-technology/components";
import { skipToken } from "@reduxjs/toolkit/query/react";
import { FilterLines, HelpCircle } from "@untitledui/icons";
import { useId, useState, type ReactNode } from "react";
import type { Key, Selection } from "react-aria-components";
import { api } from "../../state/api";
import { PlanType, type PlanResponse } from "../../state/api.types";
import { useDebounce } from "../../utilities/useDebounce";
import {
  areFiltersEqual,
  EMPTY_ACCOUNT_FILTERS,
  EXPIRING_SOON_DAYS,
  hasActiveSlideoutFilters,
  parseStatuses,
  type AccountFilters,
} from "./accountFilterHelpers";
import {
  CONTRACT_EXPIRATION_DESCRIPTION,
  FALLBACK_PLAN_DESCRIPTION,
} from "./accountPlanHelpers";
import { getStatusDisplay, ORDERED_STATUS_TYPES } from "./accountStatusHelpers";
import {
  DEVICE_FILTER_DESCRIPTION,
  getDeviceProgramLabel,
  getDeviceStatusDisplay,
  ORDERED_DEVICE_PROGRAMS,
  ORDERED_DEVICE_STATUSES,
  parseDevicePrograms,
  parseDeviceStatuses,
  toDeviceStatusParams,
  toDeviceTypes,
} from "./deviceFilterHelpers";

const statusItems = ORDERED_STATUS_TYPES.map((statusType) => {
  const display = getStatusDisplay(statusType);
  return {
    id: statusType,
    label: display.label,
    icon: <Dot className={display.dotColor} />,
  };
});

const deviceProgramItems = ORDERED_DEVICE_PROGRAMS.map((programType) => ({
  id: programType,
  label: getDeviceProgramLabel(programType),
}));

const deviceStatusItems = ORDERED_DEVICE_STATUSES.map((deviceStatus) => {
  const display = getDeviceStatusDisplay(deviceStatus);
  return {
    id: deviceStatus,
    label: display.label,
    icon: <Dot className={display.dotColor} />,
  };
});

const toOrderedIds = (items: { id: string }[], keys: Selection): string[] => {
  if (keys === "all") {
    return items.map((item) => item.id);
  }
  const selected = new Set<Key>(keys);
  return items.filter((item) => selected.has(item.id)).map((item) => item.id);
};

const FilterGroupHeading = ({
  id,
  children,
}: {
  id: string;
  children: ReactNode;
}) => (
  <span id={id} className="text-sm font-semibold text-secondary">
    {children}
  </span>
);

const FilterHelp = ({ title }: { title: string }) => (
  <Tooltip title={title} placement="top" arrow>
    <TooltipTrigger className="cursor-pointer text-fg-quaternary transition duration-200 hover:text-fg-quaternary_hover focus:text-fg-quaternary_hover">
      <HelpCircle className="size-4" />
    </TooltipTrigger>
  </Tooltip>
);

type AccountFiltersSlideoutProps = {
  appUUID: string;
  filters: AccountFilters;
  activeFilterCount: number;
  onApply: (filters: AccountFilters) => void;
  plans: PlanResponse[];
  isPlansLoading: boolean;
  search: string;
};

export const AccountFiltersSlideout = ({
  appUUID,
  filters,
  activeFilterCount,
  onApply,
  plans,
  isPlansLoading,
  search,
}: AccountFiltersSlideoutProps) => {
  const [draftFilters, setDraftFilters] = useState<AccountFilters>(filters);
  const [isOpen, setIsOpen] = useState(false);
  const statusGroupId = useId();
  const planGroupId = useId();
  const deviceGroupId = useId();

  // Deliberately not reseeded on open, so dismissing the slideout keeps an
  // unapplied draft.
  const [lastApplied, setLastApplied] = useState(filters);
  if (lastApplied !== filters) {
    setLastApplied(filters);
    setDraftFilters(filters);
  }

  const isUnchanged = areFiltersEqual(draftFilters, filters);

  const debouncedDraft = useDebounce(draftFilters, 300);
  const draftCount = api.useGetAccountsCountQuery(
    isOpen
      ? {
          appUUID,
          search,
          filter: debouncedDraft.statuses,
          plan_group_uuid: debouncedDraft.planGroupUUIDs,
          plan_type: debouncedDraft.fallbackPlanOnly
            ? PlanType.Fallback
            : undefined,
          include_fallback_plans: true,
          device_type: toDeviceTypes(debouncedDraft.devicePrograms),
          ...toDeviceStatusParams(debouncedDraft.deviceStatuses),
          expiring_in_days: debouncedDraft.upcomingExpirationsOnly
            ? EXPIRING_SOON_DAYS
            : undefined,
        }
      : skipToken,
  );

  // Every change is a new cache key, so hold the last count while the next
  // one loads rather than flickering the number away.
  const [lastCount, setLastCount] = useState<number | undefined>(undefined);
  if (draftCount.data && draftCount.data.count !== lastCount) {
    setLastCount(draftCount.data.count);
  }
  const previewCount = draftCount.data?.count ?? lastCount;

  const planItems = plans.map((plan) => ({
    id: plan.uuid,
    label: plan.name,
  }));

  return (
    <SlideoutMenu.Trigger onOpenChange={setIsOpen}>
      <Button
        size="sm"
        color="secondary"
        iconLeading={FilterLines}
        data-ph-capture-attribute-action="open-account-filters"
        className="whitespace-nowrap"
      >
        Filters
        {activeFilterCount > 0 && (
          <Badge
            size="sm"
            color="gray"
            className="ml-1 inline-flex size-[22px] items-center justify-center rounded-full bg-gray-900 p-0 text-white ring-gray-900"
          >
            {activeFilterCount}
          </Badge>
        )}
      </Button>
      <SlideoutMenu isDismissable className="max-w-xs">
        {({ close }) => (
          <>
            <SlideoutMenu.Header onClose={close}>
              <div className="text-lg font-semibold">Account filters</div>
            </SlideoutMenu.Header>
            <SlideoutMenu.Content>
              <div className="flex flex-col gap-6">
                <div
                  role="group"
                  aria-labelledby={statusGroupId}
                  className="flex flex-col gap-1.5"
                >
                  <FilterGroupHeading id={statusGroupId}>
                    Status
                  </FilterGroupHeading>
                  <MultiSelect
                    items={statusItems}
                    selectedKeys={new Set<Key>(draftFilters.statuses)}
                    onSelectionChange={(keys) => {
                      setDraftFilters((prev) => ({
                        ...prev,
                        statuses: parseStatuses(
                          toOrderedIds(statusItems, keys),
                        ),
                      }));
                    }}
                    placeholder="All statuses"
                    showSearch={false}
                    showFooter={false}
                    aria-label="Status"
                  >
                    {(item) => (
                      <MultiSelect.Item
                        id={item.id}
                        label={item.label}
                        icon={item.icon}
                      />
                    )}
                  </MultiSelect>
                </div>
                <div
                  role="group"
                  aria-labelledby={planGroupId}
                  className="flex flex-col gap-3"
                >
                  <div className="flex flex-col gap-1.5">
                    <FilterGroupHeading id={planGroupId}>
                      Plan
                    </FilterGroupHeading>
                    <MultiSelect
                      items={planItems}
                      selectedKeys={new Set(draftFilters.planGroupUUIDs)}
                      onSelectionChange={(keys) => {
                        setDraftFilters((prev) => ({
                          ...prev,
                          planGroupUUIDs: toOrderedIds(planItems, keys),
                        }));
                      }}
                      placeholder={
                        isPlansLoading ? "Loading plans…" : "All plans"
                      }
                      isDisabled={isPlansLoading}
                      showSearch={false}
                      showFooter={false}
                      aria-label="Plan"
                    >
                      {(item) => (
                        <MultiSelect.Item id={item.id} label={item.label} />
                      )}
                    </MultiSelect>
                  </div>
                  <div className="flex flex-col gap-2">
                    <div className="flex items-center gap-1.5">
                      <Toggle
                        isSelected={draftFilters.upcomingExpirationsOnly}
                        onChange={(isSelected) => {
                          setDraftFilters((prev) => ({
                            ...prev,
                            upcomingExpirationsOnly: isSelected,
                          }));
                        }}
                        size="md"
                        slim
                        label="Show only upcoming expirations"
                      />
                      <FilterHelp title={CONTRACT_EXPIRATION_DESCRIPTION} />
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Toggle
                        isSelected={draftFilters.fallbackPlanOnly}
                        onChange={(isSelected) => {
                          setDraftFilters((prev) => ({
                            ...prev,
                            fallbackPlanOnly: isSelected,
                          }));
                        }}
                        size="md"
                        slim
                        label="Show only fallback plans"
                      />
                      <FilterHelp title={FALLBACK_PLAN_DESCRIPTION} />
                    </div>
                  </div>
                </div>
                <div
                  role="group"
                  aria-labelledby={deviceGroupId}
                  className="flex flex-col gap-1.5"
                >
                  <div className="flex items-center gap-1.5">
                    <FilterGroupHeading id={deviceGroupId}>
                      Devices
                    </FilterGroupHeading>
                    <FilterHelp title={DEVICE_FILTER_DESCRIPTION} />
                  </div>
                  <MultiSelect
                    items={deviceProgramItems}
                    selectedKeys={new Set<Key>(draftFilters.devicePrograms)}
                    onSelectionChange={(keys) => {
                      setDraftFilters((prev) => ({
                        ...prev,
                        devicePrograms: parseDevicePrograms(
                          toOrderedIds(deviceProgramItems, keys),
                        ),
                      }));
                    }}
                    placeholder="All device types"
                    showSearch={false}
                    showFooter={false}
                    aria-label="Device type"
                  >
                    {(item) => (
                      <MultiSelect.Item id={item.id} label={item.label} />
                    )}
                  </MultiSelect>
                  <MultiSelect
                    items={deviceStatusItems}
                    selectedKeys={new Set<Key>(draftFilters.deviceStatuses)}
                    onSelectionChange={(keys) => {
                      setDraftFilters((prev) => ({
                        ...prev,
                        deviceStatuses: parseDeviceStatuses(
                          toOrderedIds(deviceStatusItems, keys),
                        ),
                      }));
                    }}
                    placeholder="Any device status"
                    showSearch={false}
                    showFooter={false}
                    aria-label="Device status"
                  >
                    {(item) => (
                      <MultiSelect.Item
                        id={item.id}
                        label={item.label}
                        icon={item.icon}
                      />
                    )}
                  </MultiSelect>
                </div>
              </div>
            </SlideoutMenu.Content>
            <SlideoutMenu.Footer>
              <div className="flex items-center justify-end gap-3">
                <Button
                  size="sm"
                  color="secondary"
                  isDisabled={!hasActiveSlideoutFilters(draftFilters)}
                  onClick={() => {
                    setDraftFilters(EMPTY_ACCOUNT_FILTERS);
                  }}
                >
                  Clear
                </Button>
                <Button
                  size="sm"
                  isDisabled={isUnchanged}
                  onClick={() => {
                    onApply(draftFilters);
                    close();
                  }}
                >
                  {previewCount === undefined
                    ? "View results"
                    : `View ${previewCount.toString()} ${
                        previewCount === 1 ? "result" : "results"
                      }`}
                </Button>
              </div>
            </SlideoutMenu.Footer>
          </>
        )}
      </SlideoutMenu>
    </SlideoutMenu.Trigger>
  );
};
